import { useI18n } from '@ecom/i18n';
import { Checkbox, Field, Input, Select } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { couponFormSchema, type CouponFormValues } from '../schemas';

import { whenDefined } from '@/lib/optional-props';

/**
 * `register(..., { setValueAs })` is called with whatever RHF currently
 * holds for that field — a raw DOM string once the user has touched the
 * input, but the LITERAL `defaultValues` entry (here, `null`) for a field
 * never touched. Checking only `v === ''` misses that second, very common
 * case: `Number(null)` is `0`, not `NaN`, so an untouched optional field
 * was silently coerced to 0 and failed `.positive()` — a real bug caught
 * live (submitting the create form with these three fields left empty),
 * not a hypothetical one.
 */
function nullableNumber(v: unknown): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

const EMPTY_VALUES: CouponFormValues = {
  code: '',
  type: 'fixed',
  value: 0,
  minOrderAmount: null,
  usageLimitTotal: null,
  usageLimitPerCustomer: null,
  validFrom: '',
  validUntil: '',
  isActive: true,
};

/**
 * Create AND edit share this one form (`PUT` is a full-state replace, the
 * same posture `ProductForm` already takes) — `formId` lets the caller wire
 * a `Modal` footer's submit `Button` to `form={formId}` exactly like
 * `StaffPage`'s `InviteStaffForm` does, since the submit button here lives
 * outside this `<form>` in the modal footer, not inside it.
 *
 * `code`/`type` stay editable even in edit mode: `coupon-repository.ts`'s
 * own header confirms a past `coupon_usages` row carries its own
 * `discount_amount` snapshot, so changing them afterwards only affects a
 * FUTURE redemption, never corrupts history — no field is disabled here.
 */
export function CouponForm({
  formId,
  defaultValues,
  onSubmit,
  isSubmitting,
}: {
  formId: string;
  defaultValues?: CouponFormValues;
  onSubmit: (values: CouponFormValues) => void;
  isSubmitting: boolean;
}): ReactElement {
  const { t } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CouponFormValues>({
    resolver: zodResolver(couponFormSchema),
    defaultValues: defaultValues ?? EMPTY_VALUES,
  });

  const type = watch('type');

  const typeOptions = [
    { value: 'fixed', label: t('merchant.promotions.form.typeFixed') },
    { value: 'percentage', label: t('merchant.promotions.form.typePercentage') },
  ];

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <fieldset disabled={isSubmitting} className="contents">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('merchant.marketing.table.colCode')}
            {...whenDefined('error', errors.code?.message)}
          >
            {(bind) => <Input {...bind} {...register('code')} autoFocus maxLength={50} />}
          </Field>

          <Field
            label={t('merchant.promotions.form.typeLabel')}
            {...whenDefined('error', errors.type?.message)}
          >
            {(bind) => <Select {...bind} {...register('type')} options={typeOptions} required />}
          </Field>
        </div>

        <Field
          label={
            type === 'percentage'
              ? t('merchant.promotions.form.valuePercentLabel')
              : t('merchant.promotions.form.valueAmountLabel')
          }
          {...whenDefined(
            'hint',
            type === 'percentage' ? t('merchant.promotions.form.valueHint') : undefined,
          )}
          {...whenDefined('error', errors.value?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              type="number"
              step="0.001"
              min="0"
              {...register('value', { valueAsNumber: true })}
            />
          )}
        </Field>

        <Field
          label={t('merchant.promotions.form.minOrderLabel')}
          optional
          hint={t('merchant.promotions.form.minOrderHint')}
          {...whenDefined('error', errors.minOrderAmount?.message)}
        >
          {(bind) => (
            <Input
              {...bind}
              type="number"
              step="0.001"
              min="0"
              {...register('minOrderAmount', {
                setValueAs: nullableNumber,
              })}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('merchant.promotions.form.usageLimitTotalLabel')}
            optional
            hint={t('merchant.promotions.form.usageLimitHint')}
            {...whenDefined('error', errors.usageLimitTotal?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                type="number"
                step="1"
                min="1"
                {...register('usageLimitTotal', {
                  setValueAs: nullableNumber,
                })}
              />
            )}
          </Field>

          <Field
            label={t('merchant.promotions.form.usageLimitPerCustomerLabel')}
            optional
            hint={t('merchant.promotions.form.usageLimitHint')}
            {...whenDefined('error', errors.usageLimitPerCustomer?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                type="number"
                step="1"
                min="1"
                {...register('usageLimitPerCustomer', {
                  setValueAs: nullableNumber,
                })}
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('merchant.promotions.form.validFromLabel')}
            optional
            {...whenDefined('error', errors.validFrom?.message)}
          >
            {(bind) => <Input {...bind} type="datetime-local" {...register('validFrom')} />}
          </Field>

          <Field
            label={t('merchant.promotions.form.validUntilLabel')}
            optional
            {...whenDefined('error', errors.validUntil?.message)}
          >
            {(bind) => <Input {...bind} type="datetime-local" {...register('validUntil')} />}
          </Field>
        </div>

        {/* `Checkbox`'s `onChange` hands back a plain boolean, not an Event —
            not `register`-compatible by a bare spread (see `InviteStaffForm`'s
            own permissions checkboxes for the same reason), hence `Controller`. */}
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <Checkbox
              label={t('merchant.promotions.form.activeCheckbox')}
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </fieldset>
    </form>
  );
}
